import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { getHomeSummary, getMySubmissions, getOwnerOverview } from '../api/dashboard';
import { getTeamWasteToday } from '../api/waste';
import { useAuth } from '../auth/AuthProvider';
import { canReviewWaste, canSeeActivationGuide } from '../auth/permissions';
import { BreakEvenDialog } from '../components/inicio/BreakEvenDialog';
import { InicioActivation } from '../components/inicio/InicioActivation';
import { InicioAttention } from '../components/inicio/InicioAttention';
import { InicioHero } from '../components/inicio/InicioHero';
import { InicioNotes } from '../components/inicio/InicioNotes';
import { InicioPending } from '../components/inicio/InicioPending';
import { InicioPulse, type SummaryState } from '../components/inicio/InicioPulse';
import { InicioQuickAccess } from '../components/inicio/InicioQuickAccess';
import { InicioSubmissions, hasSubmissions } from '../components/inicio/InicioSubmissions';
import { InicioWaste } from '../components/inicio/InicioWaste';
import { ResultDialog } from '../components/inicio/ResultDialog';
import type { HomeSummary, MySubmissions, OwnerOverview } from '../types/dashboard';
import type { ActivationCommercial } from '../types/onboarding';
import { commercialNote } from '../utils/activationView';
import type { WasteTeamToday } from '../types/waste';
import { heroView, notesView, resultView } from '../utils/inicioView';
import { quickAccessItems } from '../utils/quickAccess';
import { useRequestGuard } from '../utils/useRequestGuard';
import { userDisplayName } from '../utils/userDisplayName';

/**
 * Inicio — Rediseño · Bloque C (`docs/UI_REDESIGN_BLOCK_C.md`).
 *
 * La arquitectura es la del diseño aprobado (P-030, con el resumen de P-021): el hero y, debajo,
 * filas de dos tarjetas — la principal a la izquierda y la lateral a la derecha —:
 *
 *   «Así va tu negocio»   |  «Necesita tu atención»
 *   «Por resolver»        |  «Mermas de hoy»
 *   «Lo que noté»         |  «Accesos rápidos»
 *
 * La primera y la tercera fila son las de P-030 («pulse attn» / «notes quick»). La del medio lleva
 * lo que el producto tiene y la maqueta no dibuja en el Inicio: los pendientes (B0-06) y las mermas
 * del día. En una pantalla angosta todo queda en una sola columna, en ese mismo orden de lectura. No
 * hay cortes de ancho propios: cada fila se parte en dos cuando caben las dos tarjetas (`inicio.css`).
 * Una tarjeta que no aplica —sin mermas hoy, sin bloque financiero para un empleado— no deja hueco:
 * las demás suben.
 *
 * Los DATOS son los de siempre, de las mismas llamadas: nada se calcula distinto y ninguna cifra es
 * nueva. Lo que el diseño dibuja y el producto no tiene —foto del negocio, selector de tienda,
 * tarjetas favoritas, ticket promedio, insights de descubrimiento, campana de Avisos, «Nuevo
 * producto»— no se dibuja.
 *
 * «Por resolver» es el panel de pendientes con el nombre que el dueño decidió en B0-06. Ese nombre
 * sustituye al bloque general «Te toca / solo tú decides» de la maqueta; NO a «Te toca contar», que
 * es la pantalla de Conteos y sigue igual.
 *
 * Cada llamada tiene su propio estado: si una falla, las demás secciones siguen en pie y la que
 * falló lo dice, con su «Reintentar». Antes, tres de las cuatro fallaban en silencio. Y cada una
 * lleva su guardián (`useRequestGuard`): solo cuenta la respuesta de la última petición.
 */
export function DashboardHomePage({ onNavigate }: { onNavigate: (destination: string) => void }) {
  const { business, user, stores } = useAuth();
  const role = user?.role;
  const reviewsWaste = canReviewWaste(role);
  const isEmployee = role === 'EMPLOYEE';

  const [summary, setSummary] = useState<HomeSummary>();
  // P-085: la condición comercial discreta del encabezado (la cuenta la guía de activación, que ya pregunta al servidor).
  const [planNote, setPlanNote] = useState<string | null>(null);
  const onCommercial = useCallback((commercial: ActivationCommercial) => setPlanNote(commercialNote(commercial)), []);
  const [summaryState, setSummaryState] = useState<SummaryState>('loading');
  const [overview, setOverview] = useState<OwnerOverview | null>();
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);
  const [waste, setWaste] = useState<WasteTeamToday | null>(null);
  const [wasteError, setWasteError] = useState(false);
  const [submissions, setSubmissions] = useState<MySubmissions | null>(null);
  const [submissionsError, setSubmissionsError] = useState(false);
  const [dialog, setDialog] = useState<'break-even' | 'result' | null>(null);

  const summaryRequest = useRequestGuard();
  const overviewRequest = useRequestGuard();
  const wasteRequest = useRequestGuard();
  const submissionsRequest = useRequestGuard();

  const loadSummary = useCallback(() => {
    const isCurrent = summaryRequest();
    // Mientras se vuelve a pedir, lo que había se retira: no se deja a la vista, como si fuera de
    // ahora, una meta o unos pendientes que el servidor está por corregir. Cada tarjeta guarda su
    // sitio con marcadores.
    setSummary(undefined);
    setSummaryState('loading');
    getHomeSummary()
      .then((result) => { if (!isCurrent()) return; setSummary(result); setSummaryState('ready'); })
      // Si una recarga falla, lo que había ya no se sabe si sigue siendo cierto: se dice.
      .catch(() => { if (!isCurrent()) return; setSummary(undefined); setSummaryState('error'); });
  }, [summaryRequest]);

  const loadOverview = useCallback(() => {
    const isCurrent = overviewRequest();
    setOverviewLoading(true);
    setOverviewError(false);
    getOwnerOverview()
      .then((result) => { if (isCurrent()) setOverview(result ?? null); })
      .catch(() => { if (isCurrent()) setOverviewError(true); })
      .finally(() => { if (isCurrent()) setOverviewLoading(false); });
  }, [overviewRequest]);

  const loadWaste = useCallback(() => {
    const isCurrent = wasteRequest();
    setWasteError(false);
    getTeamWasteToday()
      .then((result) => { if (isCurrent()) setWaste(result); })
      .catch(() => { if (isCurrent()) setWasteError(true); });
  }, [wasteRequest]);

  const loadSubmissions = useCallback(() => {
    const isCurrent = submissionsRequest();
    setSubmissionsError(false);
    getMySubmissions()
      .then((result) => { if (isCurrent()) setSubmissions(result); })
      .catch(() => { if (isCurrent()) setSubmissionsError(true); });
  }, [submissionsRequest]);

  useEffect(() => { loadSummary(); loadOverview(); }, [loadSummary, loadOverview]);
  // Cada una se pide solo a quien le corresponde, igual que antes.
  useEffect(() => { if (reviewsWaste) loadWaste(); }, [reviewsWaste, loadWaste]);
  useEffect(() => { if (isEmployee) loadSubmissions(); }, [isEmployee, loadSubmissions]);

  const financial = summary?.financial ?? null;
  const lowStock = summary?.lowStock ?? null;
  const multiStore = (summary?.stores.length ?? stores.length) > 1;
  // Quien no tiene bloque financiero ni explicación que dar (EMPLOYEE) recibe 204: no se pinta. A un
  // empleado tampoco se le enseña el esqueleto de un bloque que no va a llegar: lo vería aparecer y
  // desaparecer. Si el servidor algún día le envía uno, se dibuja.
  const hasOverview = overview !== null && overview !== undefined;
  const showPulse = hasOverview || (!isEmployee && (overviewLoading || overviewError));
  const figures = !overviewLoading && !overviewError && hasOverview && overview.scope.mode !== 'NONE' ? overview : null;
  // Los mensajes salen de la misma llamada que el bloque: mientras carga llevan su propio marcador
  // (así las tarjetas no cambian de sitio al llegar), y si falló, eso ya se dice en el bloque.
  const notesLoading = showPulse && overviewLoading && !overviewError;
  const notes = figures ? notesView(figures) : null;
  const result = figures ? resultView(figures) : null;
  const quick = quickAccessItems(role);
  // Sin mermas vigentes no hay nada que revisar, y sin envíos no hay nada que contar: esas tarjetas
  // no se dibujan. Se decide aquí para que su sitio en la fila lo ocupe la siguiente.
  const showWaste = reviewsWaste && (wasteError || (waste !== null && waste.summary.activeCount > 0));
  const showSubmissions = isEmployee && (submissionsError || hasSubmissions(submissions));

  // Las tarjetas, en su orden de lectura. Cada fila lleva una principal y una lateral.
  const main: ReactNode[] = [];
  const side: ReactNode[] = [];
  if (showPulse) {
    main.push(
      <InicioPulse
        error={overviewError}
        financial={financial}
        loading={overviewLoading}
        onNavigate={onNavigate}
        onOpenBreakEven={() => setDialog('break-even')}
        onOpenResult={() => setDialog('result')}
        onRetry={loadOverview}
        onRetrySummary={loadSummary}
        overview={overview}
        role={role}
        summaryState={summaryState}
      />,
    );
  }
  main.push(<InicioPending data={summary?.pending} onNavigate={onNavigate} onRetry={loadSummary} role={role} state={summaryState} />);
  if (notesLoading || notes) main.push(<InicioNotes onNavigate={onNavigate} view={notes} />);
  if (showSubmissions) main.push(<InicioSubmissions data={submissions} error={submissionsError} onRetry={loadSubmissions} />);
  // Los insumos por agotarse son de dueño y gerente. Mientras el resumen carga, la tarjeta guarda su
  // sitio (así las demás no se recolocan al llegar); si el servidor no los envía, no se dibuja.
  if (lowStock || (!isEmployee && summaryState === 'loading')) side.push(<InicioAttention lowStock={lowStock} multiStore={multiStore} onNavigate={onNavigate} />);
  if (showWaste) side.push(<InicioWaste data={waste} error={wasteError} onNavigate={onNavigate} onRetry={loadWaste} stores={stores} />);
  if (quick.length > 0) side.push(<InicioQuickAccess items={quick} onNavigate={onNavigate} />);
  const rows = Array.from({ length: Math.max(main.length, side.length) }, (_, index) => ({ main: main[index], side: side[index] }));

  return (
    <div className="n3-inicio">
      <InicioHero businessName={business?.name || 'Tu negocio'} planNote={planNote} view={heroView({ firstName: user?.firstName, displayName: userDisplayName(user), role, now: new Date() })} />

      {/* ONB-001: la guía de activación del dueño de un negocio nuevo, en su propia fila. Solo se
          dibuja cuando tiene algo que decir; el resto del Inicio no cambia de sitio. */}
      {canSeeActivationGuide(role) && <InicioActivation onCommercial={onCommercial} onNavigate={onNavigate} />}

      {rows.map((row, index) => (
        <div className="n3-inicio__band" key={index}>
          {row.main && <div className="n3-inicio__cell n3-inicio__cell--main">{row.main}</div>}
          {row.side && <div className="n3-inicio__cell n3-inicio__cell--side">{row.side}</div>}
        </div>
      ))}

      {dialog === 'break-even' && financial && role === 'OWNER' && (
        <BreakEvenDialog
          current={financial.breakEven}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); loadSummary(); }}
        />
      )}
      {dialog === 'result' && result && <ResultDialog onClose={() => setDialog(null)} view={result} />}
    </div>
  );
}
